import React, { useState, useEffect, useRef } from 'react';
import { FaWater, FaWind, FaLightbulb, FaFlask, FaChartLine } from 'react-icons/fa';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

import {
  EcosystemSelector,
  EcosystemVisualization
} from './components/EcosystemComponents';

import {
  WaterPanel,
  GasesPanel,
  LightingPanel,
  AIRecommendationPanel,
  HealthPanel
} from './components/DashboardPanels';

import { ParameterCard } from './components/SharedComponents';

import { updateVitalStats } from './calculateVitalStats.js';
import { organismStates } from './organismStates.js';

const newSensorData = {
  temperature: 27,
  foodAmount: 0.2,
  light: 3200
};

updateVitalStats('Lined Seahorse', newSensorData);

console.log(organismStates['Lined Seahorse']);

function App() {
  // Refs for scrolling (only keeping ecosystem and visualization refs)
  const ecosystemSelectorRef = useRef(null);
  const visualizationRef = useRef(null);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedEcosystem, setSelectedEcosystem] = useState(null);
  const [selectedOrganism, setSelectedOrganism] = useState(null);

  const [parameters, setParameters] = useState({
    temperature: 25.0,
    salinity: 35.0,
    ph: 8.1,
    oxygen: 6.5,
    co2: 412,
    orp: 285,
    nitrate: 0.2,
    phosphate: 0.05,
    silicate: 1.8,
    pressure: 1.02,
    flowRate: 0.2,
    lightIntensity: 50,
    foodAmount: 0,
    feedingFrequency: 1,
    liveFood: true,
    brineShrimpCount: 0,
  });
  
  const [lightSpectrum, setLightSpectrum] = useState('coral-growth');
  const [aiRecommendation, setAiRecommendation] = useState('Stable conditions - no action needed');
  const [chartData, setChartData] = useState([]);

  // Using a ref to access latest parameters inside setInterval
  const parametersCurrentRef = useRef(parameters);
  useEffect(() => {
    parametersCurrentRef.current = parameters;
  }, [parameters]);

  // Modified to only scroll when selecting a new ecosystem
  const handleEcosystemSelect = (ecosystem) => {
    const isNewSelection = selectedEcosystem?.id !== ecosystem?.id;
    setSelectedEcosystem(ecosystem);
    if (isNewSelection && ecosystem) {
      setTimeout(() => {
        visualizationRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  // Modified - no scrolling on organism select
  const handleOrganismSelect = (organism) => {
    setSelectedOrganism(organism);
  };

  // Modified - no scrolling on parameter change
  const handleParameterChange = (paramName, value) => {
    setParameters(prev => ({
      ...prev,
      [paramName]: parseFloat(value),
      [`_${paramName}Locked`]: true
    }));
  };

  useEffect(() => {
    if (!selectedEcosystem) return;

    const interval = setInterval(() => {
      setParameters(prev => {
        // Only update if not locked
        const newParams = {
          ...prev,
          temperature: prev._temperatureLocked ? prev.temperature : parseFloat((prev.temperature + (Math.random() - 0.5)).toFixed(1)),
          salinity: prev._salinityLocked ? prev.salinity : parseFloat((prev.salinity + (Math.random() - 0.5)).toFixed(1)),
          ph: prev._phLocked ? prev.ph : parseFloat((prev.ph + (Math.random() - 0.5) * 0.1).toFixed(2)),
          oxygen: prev._oxygenLocked ? prev.oxygen : parseFloat((prev.oxygen + (Math.random() - 0.5) * 0.1).toFixed(1)),
          flowRate: prev._flowRateLocked ? prev.flowRate : parseFloat((prev.flowRate + (Math.random() - 0.5) * 0.1).toFixed(1)),
        };
        return newParams;
      });

      const time = new Date().toLocaleTimeString();
      setChartData(prev => {
        const newData = [...prev, { time, ...parametersCurrentRef.current }];
        return newData.length > 20 ? newData.slice(-20) : newData;
      });

      const recommendations = [
        "Stable conditions - no action needed",
        "Suggest 5% water change",
        "Increase aeration - O₂ levels dropping",
        "Add trace elements - nutrient imbalance detected",
        "Adjust lighting schedule for coral spawning",
        "Monitor salinity - approaching critical levels"
      ];
      setAiRecommendation(recommendations[Math.floor(Math.random() * recommendations.length)]);
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedEcosystem]);

  useEffect(() => {
    if (selectedEcosystem) {
      setParameters(prev => ({
        ...prev,
        ...selectedEcosystem.params,
        lightIntensity: prev.lightIntensity,
        temperature: prev.temperature,
        salinity: prev.salinity,
        ph: prev.ph,
        oxygen: prev.oxygen,
        flowRate: prev.flowRate
      }));
      setSelectedOrganism(null);
    }
  }, [selectedEcosystem]);

  // Modified - no scrolling on dose
  const handleDose = (type) => {
    setAiRecommendation(`Dosing ${type} initiated...`);
    setTimeout(() => {
      setAiRecommendation(`${type} dosing complete`);
    }, 2000);
  };

// Vitality Index Calculation - Fixed version
const vitalityIndex = Math.round(
  Math.min(
    100,
    Math.max(
      0,
      ((parameters.temperature - 20) / 10) * 25 +
      ((parameters.oxygen - 5) / 3) * 25 +
      (parameters.lightIntensity / 100) * 25 +
      (1 - Math.abs(parameters.salinity - 35) / 10) * 25
    )
  )
);

  return (
    <div className="cyberlotus-app">
      <header className="app-header">
        <div className="logo-container">
          <h1>CyberLotus</h1>
          <span>Marine Environment Control</span>
        </div>
        <div className="system-status">
          <div className="status-indicator active" />
          <span>System Online</span>
        </div>
      </header>

      <div className="app-container">
        <nav className="sidebar">
          <button
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            <FaChartLine /> Dashboard
          </button>
          <button
            className={activeTab === 'water' ? 'active' : ''}
            onClick={() => setActiveTab('water')}
            disabled
          >
            <FaWater /> Water
          </button>
          <button
            className={activeTab === 'gases' ? 'active' : ''}
            onClick={() => setActiveTab('gases')}
            disabled
          >
            <FaWind /> Gases
          </button>
          <button
            className={activeTab === 'light' ? 'active' : ''}
            onClick={() => setActiveTab('light')}
          >
            <FaLightbulb /> Lighting
          </button>
          <button
            className={activeTab === 'nutrients' ? 'active' : ''}
            onClick={() => setActiveTab('nutrients')}
            disabled
          >
            <FaFlask /> Nutrients
          </button>
        </nav>

        <main className="main-panel">
          {activeTab === 'dashboard' && (
            <>
              <div ref={ecosystemSelectorRef}>
                <EcosystemSelector
                  selectedEcosystem={selectedEcosystem}
                  onSelect={handleEcosystemSelect}
                />
              </div>

              {selectedEcosystem && (
                <>
                  <div ref={visualizationRef}>
                    <EcosystemVisualization
                      ecosystem={selectedEcosystem}
                      selectedOrganism={selectedOrganism}
                      onSelectOrganism={handleOrganismSelect}
                      parameters={parameters}
                    />
                  </div>

                  <div className="parameter-overview">
                    <h3>Current Parameters</h3>
                    <div className="parameter-grid">
                      <ParameterCard
                        name="Temperature"
                        value={`${parameters.temperature}°C`}
                        min={20}
                        max={30}
                        current={parameters.temperature}
                        unit="°C"
                        onChange={value => handleParameterChange('temperature', value)}
                      />
                      <ParameterCard
                        name="Salinity"
                        value={`${parameters.salinity} ppt`}
                        min={30}
                        max={40}
                        current={parameters.salinity}
                        unit="ppt"
                        onChange={value => handleParameterChange('salinity', value)}
                      />
                      <ParameterCard
                        name="Dissolved Oxygen"
                        value={`${parameters.oxygen} mg/L`}
                        min={5}
                        max={8}
                        current={parameters.oxygen}
                        unit="mg/L"
                        status={parameters.oxygen > 6 ? 'safe' : 'warning'}
                        onChange={value => handleParameterChange('oxygen', value)}
                      />
                      <ParameterCard
                        name="Light Intensity"
                        value={`${parameters.lightIntensity}%`}
                        min={0}
                        max={100}
                        current={parameters.lightIntensity}
                        unit="%"
                        onChange={value => handleParameterChange('lightIntensity', value)}
                      />
                    </div>
                  </div>

                  <div className="chart-container">
                    <h3>Parameter Trends</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="temperature" stroke="#ff7300" />
                        <Line type="monotone" dataKey="salinity" stroke="#387908" />
                        <Line type="monotone" dataKey="ph" stroke="#8884d8" />
                        <Line type="monotone" dataKey="oxygen" stroke="#82ca9d" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <AIRecommendationPanel 
                    recommendation={aiRecommendation} 
                    onDose={handleDose} 
                  />
                  
                  <HealthPanel 
                    vitalityIndex={vitalityIndex} 
                    selectedOrganism={selectedOrganism} 
                    selectedEcosystem={selectedEcosystem} 
                  />
                </>
              )}

              {!selectedEcosystem && (
                <p>Please select an ecosystem to view details.</p>
              )}
            </>
          )}

          {activeTab === 'light' && (
            <LightingPanel
              parameters={parameters}
              onParameterChange={handleParameterChange}
              lightSpectrum={lightSpectrum}
              setLightSpectrum={setLightSpectrum}
            />
          )}

          {['water', 'gases', 'nutrients'].includes(activeTab) && (
            <div className="disabled-tab-message">
              <h3>This feature is currently disabled</h3>
              <button onClick={() => setActiveTab('dashboard')}>
                Return to Dashboard
              </button>
            </div>
          )}
        </main>
      </div>

      <footer className="app-footer">
        <small>&copy; 2025 CyberLotus - Marine Environment Monitoring System</small>
      </footer>
    </div>
  );
}

export default App;